import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { generateArabicContract, generateArabicContractStreaming } from '../../../services/arabicContractGenerationService';
import { exportContractToPDF } from '../../../utils/pdfExportService';
import { propertyService } from '../../../services/propertyService';
import { clientService } from '../../../services/clientService';
import { saveDocuments, getDocuments } from '../../../utils/documentManager';
import { useAuth } from '../../../contexts/AuthContext';

const AIContractGeneratorModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('basic');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [generatedContract, setGeneratedContract] = useState(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    contractType: 'rental',
    partyA: '',
    partyB: '',
    propertyId: '',
    clientId: '',
    duration: '12',
    amount: '',
    terms: '',
    startDate: new Date()?.toISOString()?.split('T')?.[0],
    endDate: '',
    includeWitnesses: true,
    includeNotarization: true,
    includePaymentSchedule: true,
    includeMaintenanceClause: true,
    includeTerminationClause: true,
    customClauses: '',
    useStreaming: false
  });

  useEffect(() => {
    if (isOpen && user?.id) {
      loadData();
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (formData?.duration && formData?.startDate) {
      const start = new Date(formData.startDate);
      start?.setMonth(start?.getMonth() + parseInt(formData?.duration || 0));
      setFormData(prev => ({ ...prev, endDate: start?.toISOString()?.split('T')?.[0] }));
    }
  }, [formData?.duration, formData?.startDate]);

  const loadData = async () => {
    try {
      const [propertiesData, clientsData] = await Promise.all([
        propertyService?.getAll(user?.id),
        clientService?.getAll(user?.id)
      ]);
      setProperties(propertiesData || []);
      setClients(clientsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'propertyId' && value) {
      const property = properties?.find(p => p?.id === value);
      if (property) {
        setFormData(prev => ({
          ...prev,
          partyA: property?.owner || '',
          amount: property?.price || ''
        }));
      }
    }

    if (field === 'clientId' && value) {
      const client = clients?.find(c => c?.id === value);
      if (client) {
        setFormData(prev => ({ ...prev, partyB: client?.name || '' }));
      }
    }
  };

  const handleGenerateContract = async () => {
    try {
      setIsGenerating(true);
      setStreamingContent('');

      const selectedProperty = properties?.find(p => p?.id === formData?.propertyId);
      const selectedClient = clients?.find(c => c?.id === formData?.clientId);

      const contractData = {
        contractType: formData?.contractType,
        partyA: formData?.partyA,
        partyB: formData?.partyB,
        propertyDetails: selectedProperty,
        clientDetails: selectedClient,
        duration: formData?.duration,
        amount: formData?.amount,
        terms: formData?.terms,
        startDate: formData?.startDate,
        endDate: formData?.endDate
      };

      const templateOptions = {
        includeWitnesses: formData?.includeWitnesses,
        includeNotarization: formData?.includeNotarization,
        includePaymentSchedule: formData?.includePaymentSchedule,
        includeMaintenanceClause: formData?.includeMaintenanceClause,
        includeTerminationClause: formData?.includeTerminationClause,
        customClauses: formData?.customClauses ? formData?.customClauses?.split(',')?.map(c => c?.trim()) : []
      };

      let result;
      if (formData?.useStreaming) {
        result = await generateArabicContractStreaming(
          contractData,
          templateOptions,
          (chunk) => {
            setStreamingContent(prev => prev + chunk);
          }
        );
      } else {
        result = await generateArabicContract(contractData, templateOptions);
      }

      if (result?.success) {
        setGeneratedContract(result);
        setActiveTab('preview');

        const documents = getDocuments('property', formData?.propertyId) || [];
        documents?.push({
          id: `contract_${Date.now()}`,
          name: `عقد ${formData?.contractType === 'rental' ? 'إيجار' : 'بيع'} - ${formData?.partyB}`,
          category: 'عقود',
          uploadDate: new Date()?.toISOString(),
          size: Math.round(result?.contractHTML?.length / 1024) + ' KB',
          type: 'application/pdf',
          metadata: result?.metadata
        });
        saveDocuments('property', formData?.propertyId, documents);
      }
    } catch (error) {
      alert(error?.message || 'فشل في إنشاء العقد. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportPDF = async () => {
    if (!generatedContract?.contractHTML) {
      alert('لا يوجد عقد لتصديره');
      return;
    }

    try {
      setIsExporting(true);
      await exportContractToPDF(generatedContract?.contractHTML, generatedContract?.metadata);
      alert('تم تصدير العقد بنجاح');
    } catch (error) {
      alert(error?.message || 'فشل في تصدير العقد');
    } finally {
      setIsExporting(false);
    }
  };

  const contractTypes = [
    { value: 'rental', label: 'عقد إيجار' },
    { value: 'sale', label: 'عقد بيع' },
    { value: 'partnership', label: 'عقد شراكة' },
    { value: 'management', label: 'عقد إدارة' }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="إنشاء عقد قانوني بالذكاء الاصطناعي"
      icon={null}
      iconColor={null}
      footer={null}
      size="large"
    >
      <div className="space-y-6">
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setActiveTab('basic')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'basic' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            معلومات أساسية
          </button>
          <button
            onClick={() => setActiveTab('template')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'template' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            تخصيص القالب
          </button>
          {generatedContract && (
            <button
              onClick={() => setActiveTab('preview')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'preview' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              معاينة العقد
            </button>
          )}
        </div>

        {activeTab === 'basic' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">نوع العقد</label>
                <select
                  value={formData?.contractType}
                  onChange={(e) => handleInputChange('contractType', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                >
                  {contractTypes?.map((type) => (
                    <option key={type?.value} value={type?.value}>
                      {type?.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">اختر عقار</label>
                <select
                  value={formData?.propertyId}
                  onChange={(e) => handleInputChange('propertyId', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                >
                  <option value="">اختر عقار...</option>
                  {properties?.map((property) => (
                    <option key={property?.id} value={property?.id}>
                      {property?.title} - {property?.city}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">اختر عميل</label>
                <select
                  value={formData?.clientId}
                  onChange={(e) => handleInputChange('clientId', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                >
                  <option value="">اختر عميل...</option>
                  {clients?.map((client) => (
                    <option key={client?.id} value={client?.id}>
                      {client?.name} - {client?.phone}
                    </option>
                  ))}
                </select>
              </div>

              <Input
                label="الطرف الأول (المالك)"
                value={formData?.partyA}
                onChange={(e) => handleInputChange('partyA', e?.target?.value)}
                placeholder="اسم المالك"
              />

              <Input
                label="الطرف الثاني (العميل)"
                value={formData?.partyB}
                onChange={(e) => handleInputChange('partyB', e?.target?.value)}
                placeholder="اسم العميل"
              />

              <Input
                label="المدة (بالأشهر)"
                type="number"
                value={formData?.duration}
                onChange={(e) => handleInputChange('duration', e?.target?.value)}
                placeholder="12"
              />

              <Input
                label="المبلغ (دينار أردني)"
                type="number"
                value={formData?.amount}
                onChange={(e) => handleInputChange('amount', e?.target?.value)}
                placeholder="0"
              />

              <Input
                label="تاريخ البدء"
                type="date"
                value={formData?.startDate}
                onChange={(e) => handleInputChange('startDate', e?.target?.value)}
              />

              <Input
                label="تاريخ الانتهاء"
                type="date"
                value={formData?.endDate}
                onChange={(e) => handleInputChange('endDate', e?.target?.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">شروط إضافية</label>
              <textarea
                value={formData?.terms}
                onChange={(e) => handleInputChange('terms', e?.target?.value)}
                placeholder="أي شروط أو بنود إضافية..."
                rows={4}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground resize-none"
              />
            </div>
          </div>
        )}

        {activeTab === 'template' && (
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Settings" size={16} />
                خيارات القالب
              </h3>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.includeWitnesses}
                    onChange={(e) => handleInputChange('includeWitnesses', e?.target?.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm text-foreground">إضافة قسم الشهود</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.includeNotarization}
                    onChange={(e) => handleInputChange('includeNotarization', e?.target?.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm text-foreground">إضافة قسم التوثيق الرسمي</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.includePaymentSchedule}
                    onChange={(e) => handleInputChange('includePaymentSchedule', e?.target?.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm text-foreground">إضافة جدول الدفعات</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.includeMaintenanceClause}
                    onChange={(e) => handleInputChange('includeMaintenanceClause', e?.target?.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm text-foreground">إضافة بند الصيانة</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.includeTerminationClause}
                    onChange={(e) => handleInputChange('includeTerminationClause', e?.target?.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm text-foreground">إضافة بند إنهاء العقد</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.useStreaming}
                    onChange={(e) => handleInputChange('useStreaming', e?.target?.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm text-foreground">إنشاء فوري (معاينة مباشرة)</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                بنود مخصصة (مفصولة بفاصلة)
              </label>
              <textarea
                value={formData?.customClauses}
                onChange={(e) => handleInputChange('customClauses', e?.target?.value)}
                placeholder="بند 1, بند 2, بند 3..."
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground resize-none"
              />
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="#3B82F6" />
                <div>
                  <h4 className="text-sm font-semibold text-foreground">ملاحظة قانونية</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    جميع العقود المُنشأة بالذكاء الاصطناعي يجب مراجعتها من قبل مستشار قانوني قبل التوقيع
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'preview' && generatedContract && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">معاينة العقد</h3>
              <Button
                variant="default"
                onClick={handleExportPDF}
                disabled={isExporting}
                iconName="Download"
                iconSize={16}
              >
                {isExporting ? 'جاري التصدير...' : 'تصدير PDF'}
              </Button>
            </div>

            <div
              className="max-h-[500px] overflow-auto bg-white rounded-lg p-6 border border-border"
              dir="rtl"
              dangerouslySetInnerHTML={{ __html: formData?.useStreaming ? streamingContent : generatedContract?.contractHTML }}
            />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            إلغاء
          </Button>
          {activeTab !== 'preview' && (
            <Button
              variant="default"
              onClick={handleGenerateContract}
              disabled={isGenerating || !formData?.partyA || !formData?.partyB || !formData?.amount}
              iconName="Wand2"
              iconSize={16}
            >
              {isGenerating ? 'جاري الإنشاء...' : 'إنشاء العقد'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AIContractGeneratorModal;